import { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import type { NavSectionConfig } from "@/types/navigation";
import { NavItem } from "./NavItem";
import { cn } from "@/lib/utils";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

const STORAGE_KEY_PREFIX = "holescale-sidebar-section-open:";

function getStoredSectionOpen(sectionId: string): boolean {
  if (typeof window === "undefined") return true;
  try {
    const stored = localStorage.getItem(STORAGE_KEY_PREFIX + sectionId);
    return stored === null ? true : stored === "true";
  } catch {
    return true;
  }
}

function setStoredSectionOpen(sectionId: string, open: boolean) {
  try {
    localStorage.setItem(STORAGE_KEY_PREFIX + sectionId, String(open));
  } catch {
    /* ignore */
  }
}

interface NavSectionProps {
  section: NavSectionConfig;
  onNavigate?: () => void;
  collapsed?: boolean;
  isFirstSection?: boolean;
}

export function NavSection({ section, onNavigate, collapsed = false, isFirstSection = false }: NavSectionProps) {
  const headingId = section.label ? `${section.id}-section-label` : undefined;
  const contentId = section.id + "-section-menu";
  const isQuickActions = section.id === "quick-actions";

  const [isOpen, setIsOpen] = useState(() =>
    section.collapsed === true ? false : getStoredSectionOpen(section.id)
  );

  useEffect(() => {
    setStoredSectionOpen(section.id, isOpen);
  }, [section.id, isOpen]);

  const isCollapsible = !collapsed && section.label != null && !isQuickActions;

  return (
    <section
      className={cn(
        collapsed ? "w-full flex flex-col items-center flex-shrink-0" : "space-y-0.5 overflow-visible",
        !collapsed && !isQuickActions && "py-0",
        isQuickActions && !collapsed && "bg-white rounded-lg p-4 -mx-1 border border-border"
      )}
      aria-labelledby={headingId}
      aria-expanded={isCollapsible ? isOpen : undefined}
    >
      {section.label && !collapsed && (
        isCollapsible ? (
          <Collapsible open={isOpen} onOpenChange={setIsOpen}>
            <CollapsibleTrigger
              asChild
              id={headingId}
              aria-controls={contentId}
              aria-expanded={isOpen}
            >
              <button
                type="button"
                className={cn(
                  "flex w-full items-center gap-2 py-2 px-2 rounded-md min-w-0",
                  "text-xs font-medium tracking-wider text-muted-foreground uppercase",
                  "hover:text-foreground hover:bg-muted/80",
                  "border-l-2 border-transparent hover:border-primary/30",
                  "transition-colors duration-150 transition-transform duration-200",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                )}
              >
                <span className="min-w-0 flex-1 truncate text-left">{section.label}</span>
                <ChevronDown
                  className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200", !isOpen && "rotate-[-90deg]")}
                  aria-hidden
                />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent
              id={contentId}
              className="overflow-hidden transition-all duration-200 data-[state=closed]:hidden"
            >
              <ul className="w-full space-y-0.5 pt-0.5" role="list">
                {section.items.map((item, itemIndex) => (
                  <li
                    key={item.href}
                    className={cn(
                      collapsed ? "flex justify-center w-full my-[5px]" : "w-full",
                      !collapsed && itemIndex > 0 && "mt-0"
                    )}
                  >
                    <NavItem
                      item={item}
                      onNavigate={onNavigate}
                      collapsed={collapsed}
                      isInQuickActions={isQuickActions}
                    />
                  </li>
                ))}
              </ul>
            </CollapsibleContent>
          </Collapsible>
        ) : (
          <>
            <div
              id={headingId}
              className={cn(
                "flex items-center gap-2 py-2 px-2 min-w-0",
                "text-xs font-medium tracking-wider text-muted-foreground uppercase",
                "border-l-2 border-transparent"
              )}
            >
              <span className="flex-1 truncate">{section.label}</span>
            </div>
            <ul id={contentId} className="w-full space-y-0.5" role="list">
              {section.items.map((item, itemIndex) => (
                <li
                  key={item.href}
                  className={cn(
                    collapsed ? "flex justify-center w-full my-[5px]" : "w-full",
                    !collapsed && itemIndex > 0 && "mt-0"
                  )}
                >
                  <NavItem
                    item={item}
                    onNavigate={onNavigate}
                    collapsed={collapsed}
                    isInQuickActions={isQuickActions}
                  />
                </li>
              ))}
            </ul>
          </>
        )
      )}
      {(!section.label || collapsed) && (
        <ul
          id={contentId}
          className={cn(
            "w-full",
            collapsed ? "flex flex-col items-center gap-1" : "space-y-0.5",
            isCollapsible && !isOpen && "hidden"
          )}
          role="list"
        >
          {section.items.map((item, itemIndex) => (
            <li
              key={item.href}
              className={cn(
                collapsed ? "flex justify-center w-full my-[5px]" : "w-full",
                !collapsed && itemIndex > 0 && "mt-0"
              )}
            >
              <NavItem
                item={item}
                onNavigate={onNavigate}
                collapsed={collapsed}
                isInQuickActions={isQuickActions}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
